import { useState } from 'react';
import { toast } from '@/hooks/ui/use-toast';
import type {
  AttendanceSyncMode,
  AttendanceSyncResult,
  CreateShiftsSyncResult,
  MatchExistingSyncResult,
  LegacySyncResult,
  SyncTimeMode,
} from '@/types/dotypos';

interface UseDotyposAttendanceSyncReturn {
  isSyncing: boolean;
  lastSyncResult: AttendanceSyncResult | null;
  syncAttendance: (
    businessId: string,
    dateFrom?: string,
    dateTo?: string
  ) => Promise<LegacySyncResult | null>;
  syncAttendanceWithShifts: (
    businessId: string,
    mode: AttendanceSyncMode,
    syncTimeMode?: SyncTimeMode,
    dateFrom?: string,
    dateTo?: string
  ) => Promise<CreateShiftsSyncResult | MatchExistingSyncResult | null>;
}

export const useDotyposAttendanceSync = (): UseDotyposAttendanceSyncReturn => {
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncResult, setLastSyncResult] = useState<AttendanceSyncResult | null>(null);

  // Legacy sync - just creates time_logs without shift association
  const syncAttendance = async (
    businessId: string,
    dateFrom?: string,
    dateTo?: string
  ): Promise<LegacySyncResult | null> => {
    setIsSyncing(true);

    try {
      const apiBase = import.meta.env.VITE_PASS_GENERATOR_URL || 'http://localhost:3001';

      const response = await fetch(`${apiBase}/dotypos/sync-attendance`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          business_id: businessId,
          date_from: dateFrom,
          date_to: dateTo,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to sync attendance');
      }

      if (data.success) {
        setLastSyncResult(data.summary);
        toast.success('Dotypos attendance synced', {
          description: `Synced ${data.summary.synced} records. ${data.summary.skipped} skipped, ${data.summary.errors} errors.`,
        });
        return data.summary;
      } else {
        throw new Error(data.error || 'Sync failed');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      toast.error('Sync failed', {
        description: errorMessage,
      });
      return null;
    } finally {
      setIsSyncing(false);
    }
  };

  // Enhanced sync - creates shifts or matches to existing shifts
  const syncAttendanceWithShifts = async (
    businessId: string,
    mode: AttendanceSyncMode,
    syncTimeMode?: SyncTimeMode,
    dateFrom?: string,
    dateTo?: string
  ): Promise<CreateShiftsSyncResult | MatchExistingSyncResult | null> => {
    setIsSyncing(true);

    try {
      const apiBase = import.meta.env.VITE_PASS_GENERATOR_URL || 'http://localhost:3001';

      const response = await fetch(`${apiBase}/dotypos/sync-attendance-with-shifts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          business_id: businessId,
          mode,
          sync_mode: syncTimeMode,
          date_from: dateFrom,
          date_to: dateTo,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to sync attendance');
      }

      if (data.success) {
        setLastSyncResult(data.summary);

        if (mode === 'create_shifts') {
          const result = data.summary as CreateShiftsSyncResult;
          toast.success('Shifts created from Dotypos attendance', {
            description: `Created ${result.shifts_created} shifts with ${result.time_logs_created} time logs. ${result.skipped_duplicates} duplicates skipped.`,
          });
        } else {
          const result = data.summary as MatchExistingSyncResult;
          toast.success('Attendance matched to shifts', {
            description: `Matched ${result.matched_to_shifts} records to shifts. ${result.unmatched} unmatched, ${result.skipped} skipped.`,
          });
        }

        return data.summary;
      } else {
        throw new Error(data.error || 'Sync failed');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      toast.error('Sync failed', {
        description: errorMessage,
      });
      return null;
    } finally {
      setIsSyncing(false);
    }
  };

  return {
    isSyncing,
    lastSyncResult,
    syncAttendance,
    syncAttendanceWithShifts,
  };
};
